<!-- src/shared/components/layout/TransactionalFooter.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import JurisdictionToggle from '@/shared/components/ui/JurisdictionToggle.vue';
  import LanguageToggle from '@/shared/components/ui/LanguageToggle.vue';
  import FooterLinks from '@/shared/components/layout/FooterLinks.vue';
  import FeedbackToggle from '@/shared/components/ui/FeedbackToggle.vue';
  import ThemeToggle from '@/shared/components/ui/ThemeToggle.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import type { LayoutProps } from '@/types/ui/layouts';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';
  import { RouterLink } from 'vue-router';

  withDefaults(defineProps<LayoutProps>(), {
    displayFeedback: true,
    displayFooterLinks: true,
    displayVersion: true,
    displayToggles: true,
    displayPoweredBy: true,
  });

  const { t } = useI18n();
  const bootstrapStore = useBootstrapStore();
  const {
    regions_enabled,
    regions,
    authentication,
    i18n_enabled,
    ui,
  } = storeToRefs(bootstrapStore);

  const { isCustom } = storeToRefs(useProductIdentity());

  // Hide regions toggle on custom domains (they're tied to a specific deployment)
  const showRegionsToggle = computed(
    () => regions_enabled.value && !!regions?.value && !isCustom.value
  );
</script>

<template>
  <!-- prettier-ignore-attribute class -->
  <footer
    class="
    w-full min-w-[320px]
    py-16 transition-all
    duration-300"
    style="background-color: transparent; background-image: linear-gradient(176deg, #1B509E 0%, #0F4089 24%);"
    :aria-label="t('web.layout.site_footer')">
    <div class="container mx-auto max-w-2xl px-4">
      <!-- Footer Links Section -->
      <FooterLinks v-if="displayFooterLinks" />

      <!-- Existing Footer Content -->
      <!-- prettier-ignore-attribute class -->
      <div
        class="
        flex
        flex-col-reverse items-center
        space-y-6 space-y-reverse md:flex-row
        md:space-y-0"
        :class="[
          'justify-between',
          displayFooterLinks && ui?.footer_links?.enabled
            ? 'mt-8 border-t border-white/20 pt-8'
            : '',
        ]">
        <!-- Legal links and copyright -->
        <!-- prettier-ignore-attribute class -->
        <div
          class="
          flex w-full flex-col
          flex-wrap items-center justify-center
          gap-3 text-center
          text-white md:w-auto md:items-start md:justify-start md:text-left"
          style="font-size: 14px;">
          <nav
            class="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 md:justify-start"
            aria-label="Rechtliche Links">
            <a
              href="https://alternita.de/impressum/"
              class="text-white/80 transition-colors hover:text-white">
              Impressum
            </a>
            <a
              href="https://alternita.de/datenschutz/"
              class="text-white/80 transition-colors hover:text-white">
              Datenschutz
            </a>
          </nav>
          <div>© 2026 ALTERNITA DIENSTLEISTUNGS-GMBH. Alle Rechte vorbehalten.</div>
        </div>

        <!-- Toggles Section -->
        <!-- prettier-ignore-attribute class -->
        <div
          v-if="displayToggles"
          class="flex w-full flex-row items-center justify-center gap-4 sm:w-auto sm:justify-end">
          <JurisdictionToggle v-if="showRegionsToggle" />

          <!-- prettier-ignore-attribute class -->
          <ThemeToggle :aria-label="t('web.layout.toggle_dark_mode')" />

          <LanguageToggle
            v-if="i18n_enabled"
            :compact="true"
            max-height="max-h-dvh" />

          <FeedbackToggle
            v-if="displayFeedback && authentication?.enabled"
            style="display: none;"
            class="text-gray-500 transition-colors duration-200 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
            :aria-label="t('web.layout.provide_feedback')" />
        </div>
      </div>
    </div>
  </footer>
</template>
